import { Fragment, useEffect, useState } from "react";
import { ChevronDown, ChevronUp, Download, FileEdit, LogOut, Loader2, RotateCcw, Save, Users, Settings as SettingsIcon } from "lucide-react";
import { toast } from "sonner";
import Layout from "../components/Layout";
import { API_BASE, adminApi, currency, multiple, percent } from "../lib/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";

const TOKEN_KEY = "gymsales_admin_token";

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [creds, setCreds] = useState({ username: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState(null);
  const [leads, setLeads] = useState([]);
  const [interimLead, setInterimLead] = useState(null);
  const [expandedLead, setExpandedLead] = useState(null);
  const [regenBusy, setRegenBusy] = useState(null);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(creds),
      });
      if (!res.ok) throw new Error("Invalid credentials");
      const data = await res.json();
      localStorage.setItem(TOKEN_KEY, data.token);
      localStorage.setItem("gymsales_admin_user", data.username || "");
      localStorage.setItem("gymsales_admin_role", data.role || "");
      setToken(data.token);
      toast.success(`Signed in as ${data.username || "admin"}.`);
    } catch (e) {
      toast.error(e.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem("gymsales_admin_user");
    localStorage.removeItem("gymsales_admin_role");
    setToken("");
    setSettings(null);
    setLeads([]);
  };

  const loadAll = async () => {
    if (!token) return;
    try {
      const c = adminApi(token);
      const [s, l] = await Promise.all([
        c.get("/admin/settings"),
        c.get("/admin/leads"),
      ]);
      setSettings(s.data);
      setLeads(l.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        toast.error("Session expired. Please sign in again.");
        logout();
      }
    }
  };

  useEffect(() => { loadAll(); }, [token]);

  const saveSettings = async () => {
    try {
      const c = adminApi(token);
      const patch = {
        replacement_salary: Number(settings.replacement_salary),
        min_sample_size: Number(settings.min_sample_size),
        disclaimer: settings.disclaimer,
        notification_email: settings.notification_email,
        benchmarks: settings.benchmarks,
        anchors: settings.anchors,
        equipment_useful_life_years: Number(settings.equipment_useful_life_years) || 10,
        equipment_levels: settings.equipment_levels,
        equipment_condition_adj: settings.equipment_condition_adj,
      };
      const res = await c.put("/admin/settings", patch);
      setSettings(res.data);
      toast.success("Settings saved.");
    } catch (e) {
      toast.error("Failed to save settings");
    }
  };

  const exportCsv = () => {
    window.open(`${API_BASE}/admin/leads/export?_t=${token}`, "_blank");
    // header-based auth for streaming download — using fetch instead:
    fetch(`${API_BASE}/admin/leads/export`, {
      headers: { "X-Admin-Token": token },
    }).then((r) => r.blob()).then((blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "gymsales-leads.csv";
      a.click();
      URL.revokeObjectURL(url);
    });
  };

  const reDownloadInterim = async (lead, entry, key) => {
    setRegenBusy(key);
    try {
      const res = await fetch(`${API_BASE}/admin/valuations/${lead.id}/interim-report`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Admin-Token": token },
        body: JSON.stringify({
          low_multiple: entry.low_multiple,
          high_multiple: entry.high_multiple,
          broker_notes: entry.broker_notes || "",
          re_download: true,
        }),
      });
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `GymSales-Interim-${lead.id.slice(0, 8)}-${new Date(entry.generated_at).toISOString().slice(0,10)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Interim PDF re-downloaded.");
    } catch (e) {
      toast.error(e.message || "Failed to re-download interim report");
    } finally {
      setRegenBusy(null);
    }
  };

  if (!token) {
    return (
      <Layout>
        <div className="mx-auto max-w-md px-6 py-16">
          <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
            <h2 className="font-display text-2xl font-bold text-slate-900">Administrator sign in</h2>
            <p className="mt-2 text-sm text-slate-600">Access valuations, leads and configuration.</p>
            <form className="mt-6 space-y-4" onSubmit={login}>
              <div>
                <Label className="mb-2 block">Username</Label>
                <Input value={creds.username} onChange={(e) => setCreds({ ...creds, username: e.target.value })}
                  data-testid="admin-username" className="h-12" />
              </div>
              <div>
                <Label className="mb-2 block">Password</Label>
                <Input type="password" value={creds.password} onChange={(e) => setCreds({ ...creds, password: e.target.value })}
                  data-testid="admin-password" className="h-12" />
              </div>
              <button type="submit" data-testid="admin-login-btn" className="btn-primary w-full" disabled={loading}>
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
              </button>
            </form>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="mx-auto max-w-6xl px-6 py-10">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <div className="overline">Admin dashboard</div>
            <h2 className="font-display text-3xl font-bold text-slate-900">GymSales settings & leads</h2>
          </div>
          <button onClick={logout} data-testid="admin-logout-btn" className="btn-ghost">
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>

        <Tabs defaultValue="leads" className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="leads" data-testid="tab-leads"><Users className="mr-2 h-4 w-4" /> Leads</TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings"><SettingsIcon className="mr-2 h-4 w-4" /> Settings</TabsTrigger>
            <TabsTrigger value="benchmarks" data-testid="tab-benchmarks">Benchmarks</TabsTrigger>
            <TabsTrigger value="anchors" data-testid="tab-anchors">Anchors</TabsTrigger>
            <TabsTrigger value="equipment" data-testid="tab-equipment">Equipment</TabsTrigger>
          </TabsList>

          <TabsContent value="leads">
            <div className="mb-4 flex items-center justify-between">
              <div className="text-sm text-slate-600">{leads.length} valuation{leads.length === 1 ? "" : "s"}</div>
              <button onClick={exportCsv} data-testid="admin-export-btn" className="btn-ghost">
                <Download className="h-4 w-4" /> Export CSV
              </button>
            </div>
            <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
                    <th className="px-4 py-3">Date</th>
                    <th className="px-4 py-3">Name</th>
                    <th className="px-4 py-3">Email</th>
                    <th className="px-4 py-3">Phone</th>
                    <th className="px-4 py-3">Business</th>
                    <th className="px-4 py-3">State</th>
                    <th className="px-4 py-3">SDE</th>
                    <th className="px-4 py-3">Central</th>
                    <th className="px-4 py-3">Multiple</th>
                    <th className="px-4 py-3">Score</th>
                    <th className="px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {leads.length === 0 ? (
                    <tr><td colSpan={11} className="px-4 py-8 text-center text-slate-500">No leads yet.</td></tr>
                  ) : leads.map((l) => {
                    const history = l.interim_reports || [];
                    const isOpen = expandedLead === l.id;
                    return (
                      <Fragment key={l.id}>
                        <tr className="hover:bg-slate-50">
                          <td className="px-4 py-3 text-slate-500">{new Date(l.created_at).toLocaleDateString("en-AU")}</td>
                          <td className="px-4 py-3 font-medium text-slate-800">
                            <div className="flex items-center gap-2">
                              {history.length > 0 && (
                                <button
                                  onClick={() => setExpandedLead(isOpen ? null : l.id)}
                                  data-testid={`expand-btn-${l.id}`}
                                  className="text-slate-400 hover:text-slate-700"
                                  aria-label="Toggle interim history"
                                >
                                  {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                                </button>
                              )}
                              <span>{l.lead?.first_name} {l.lead?.last_name}</span>
                              {history.length > 0 && (
                                <span className="rounded-full bg-orange-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-orange-700">
                                  {history.length} interim
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-slate-700">{l.lead?.email}</td>
                          <td className="px-4 py-3 text-slate-700">
                            {l.lead?.phone ? (
                              <a
                                href={`tel:${(l.lead.phone || "").replace(/[^\d+]/g, "")}`}
                                className="text-slate-800 hover:text-orange-600"
                                data-testid={`lead-phone-${l.id}`}
                              >
                                {l.lead.phone}
                              </a>
                            ) : (
                              <span className="text-slate-400">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-slate-700">{l.lead?.business_name}</td>
                          <td className="px-4 py-3 text-slate-700">{l.lead?.state}</td>
                          <td className="px-4 py-3 text-slate-700">{currency(l.result?.normalised_pebitda)}</td>
                          <td className="px-4 py-3 font-semibold text-slate-900">{currency(l.result?.central_value)}</td>
                          <td className="px-4 py-3 text-slate-700">
                            {l.result?.adjusted_multiple_low && multiple(l.result.adjusted_multiple_low)} – {l.result?.adjusted_multiple_high && multiple(l.result.adjusted_multiple_high)}
                          </td>
                          <td className="px-4 py-3 text-slate-700">{l.result?.saleability_score ?? "—"}</td>
                          <td className="px-4 py-3">
                            <button
                              onClick={() => setInterimLead(l)}
                              data-testid={`interim-btn-${l.id}`}
                              className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                            >
                              <FileEdit className="h-3.5 w-3.5" /> Interim
                            </button>
                          </td>
                        </tr>
                        {isOpen && history.length > 0 && (
                          <tr key={`${l.id}-history`} className="bg-slate-50/70">
                            <td colSpan={11} className="px-4 py-4">
                              <div className="rounded-lg border border-slate-200 bg-white p-3">
                                <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
                                  Interim report history
                                </div>
                                <table className="w-full text-xs">
                                  <thead>
                                    <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">
                                      <th className="py-1.5">Date</th>
                                      <th className="py-1.5">Broker</th>
                                      <th className="py-1.5">Multiples</th>
                                      <th className="py-1.5">Central</th>
                                      <th className="py-1.5">Range</th>
                                      <th className="py-1.5">Notes</th>
                                      <th className="py-1.5"></th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-slate-100">
                                    {[...history].reverse().map((h, i) => {
                                      const originalIndex = history.length - 1 - i;
                                      const key = `${l.id}-${originalIndex}`;
                                      return (
                                        <tr key={key} data-testid={`interim-history-${l.id}-${originalIndex}`}>
                                          <td className="py-1.5 text-slate-600">
                                            {new Date(h.generated_at).toLocaleString("en-AU", {
                                              day: "2-digit", month: "short", year: "numeric",
                                              hour: "2-digit", minute: "2-digit",
                                            })}
                                          </td>
                                          <td className="py-1.5 text-slate-700">{h.generated_by || "—"}</td>
                                          <td className="py-1.5 text-slate-700">
                                            {multiple(h.low_multiple)} – {multiple(h.high_multiple)}
                                          </td>
                                          <td className="py-1.5 font-semibold text-slate-900">{currency(h.central_value)}</td>
                                          <td className="py-1.5 text-slate-600">{currency(h.low_value)} – {currency(h.high_value)}</td>
                                          <td className="py-1.5 max-w-[220px] truncate text-slate-500" title={h.broker_notes}>
                                            {h.broker_notes || "—"}
                                          </td>
                                          <td className="py-1.5">
                                            <button
                                              onClick={() => reDownloadInterim(l, h, key)}
                                              disabled={regenBusy === key}
                                              data-testid={`redownload-btn-${l.id}-${originalIndex}`}
                                              className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                                            >
                                              {regenBusy === key
                                                ? <Loader2 className="h-3 w-3 animate-spin" />
                                                : <RotateCcw className="h-3 w-3" />}
                                              Re-download
                                            </button>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="settings">
            {settings && (
              <div className="space-y-6 rounded-2xl border border-slate-200 bg-white p-6">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <Label className="mb-2 block">Replacement manager salary (AUD, incl. super)</Label>
                    <Input type="number" value={settings.replacement_salary || ""}
                      onChange={(e) => setSettings({ ...settings, replacement_salary: e.target.value })}
                      data-testid="setting-salary" className="h-12" />
                  </div>
                  <div>
                    <Label className="mb-2 block">Minimum benchmark sample size</Label>
                    <Input type="number" value={settings.min_sample_size || 3}
                      onChange={(e) => setSettings({ ...settings, min_sample_size: e.target.value })}
                      data-testid="setting-sample" className="h-12" />
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="mb-2 block">Lead notification email</Label>
                    <Input value={settings.notification_email || ""}
                      onChange={(e) => setSettings({ ...settings, notification_email: e.target.value })}
                      data-testid="setting-notify" className="h-12" />
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="mb-2 block">Disclaimer text</Label>
                    <Textarea rows={7} value={settings.disclaimer || ""}
                      onChange={(e) => setSettings({ ...settings, disclaimer: e.target.value })}
                      data-testid="setting-disclaimer" />
                  </div>
                </div>
                <div className="flex justify-end">
                  <button onClick={saveSettings} className="btn-primary" data-testid="save-settings-btn">
                    <Save className="h-4 w-4" /> Save changes
                  </button>
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="benchmarks">
            {settings?.benchmarks && (
              <BenchmarksEditor
                benchmarks={settings.benchmarks}
                onChange={(b) => setSettings({ ...settings, benchmarks: b })}
                onSave={saveSettings}
              />
            )}
          </TabsContent>

          <TabsContent value="anchors">
            {settings && (
              <AnchorsEditor
                anchors={settings.anchors}
                onChange={(a) => setSettings({ ...settings, anchors: a })}
                onSave={saveSettings}
              />
            )}
          </TabsContent>

          <TabsContent value="equipment">
            {settings && (
              <EquipmentEditor
                settings={settings}
                onChange={(s) => setSettings({ ...settings, ...s })}
                onSave={saveSettings}
              />
            )}
          </TabsContent>
        </Tabs>
      </div>
      <InterimReportDialog
        lead={interimLead}
        onClose={() => setInterimLead(null)}
        token={token}
      />
    </Layout>
  );
}

function BenchmarksEditor({ benchmarks, onChange, onSave }) {
  const update = (key, field, value) => {
    const copy = { ...benchmarks };
    const b = { ...(copy[key] || { values: {} }) };
    if (field === "sample_size") {
      b.sample_size = Number(value);
    } else if (field === "gym_type_label") {
      b.gym_type_label = value;
    } else {
      const v = value === "" ? null : Number(value);
      b.values = { ...(b.values || {}), [field]: v };
    }
    copy[key] = b;
    onChange(copy);
  };

  const rows = Object.entries(benchmarks || {});
  return (
    <div className="space-y-4">
      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
              <th className="px-4 py-3">Gym type</th>
              <th className="px-4 py-3">Revenue / member</th>
              <th className="px-4 py-3">SDE margin</th>
              <th className="px-4 py-3">Members / employee</th>
              <th className="px-4 py-3">Growth rate</th>
              <th className="px-4 py-3">Sample size</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map(([key, b]) => (
              <tr key={key}>
                <td className="px-4 py-3 font-medium text-slate-800">{b.gym_type_label || key}</td>
                <td className="px-4 py-3"><Input type="number" value={b.values?.revenue_per_member ?? ""}
                  onChange={(e) => update(key, "revenue_per_member", e.target.value)} className="h-10" /></td>
                <td className="px-4 py-3"><Input type="number" step="0.01" value={b.values?.pebitda_margin ?? ""}
                  onChange={(e) => update(key, "pebitda_margin", e.target.value)} className="h-10" /></td>
                <td className="px-4 py-3"><Input type="number" value={b.values?.members_per_employee ?? ""}
                  onChange={(e) => update(key, "members_per_employee", e.target.value)} className="h-10" /></td>
                <td className="px-4 py-3"><Input type="number" step="0.01" value={b.values?.member_growth_rate ?? ""}
                  onChange={(e) => update(key, "member_growth_rate", e.target.value)} className="h-10" /></td>
                <td className="px-4 py-3"><Input type="number" value={b.sample_size ?? 0}
                  onChange={(e) => update(key, "sample_size", e.target.value)} className="h-10 w-24" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex justify-end">
        <button onClick={onSave} className="btn-primary"><Save className="h-4 w-4" /> Save benchmarks</button>
      </div>
    </div>
  );
}

function AnchorsEditor({ anchors, onChange, onSave }) {
  const list = anchors || [
    { pebitda: 50000, low: 1.25, high: 1.5 },
    { pebitda: 75000, low: 1.38, high: 1.6 },
    { pebitda: 100000, low: 1.7, high: 2.0 },
    { pebitda: 125000, low: 1.85, high: 2.3 },
    { pebitda: 150000, low: 2.0, high: 2.5 },
    { pebitda: 200000, low: 2.1, high: 2.5 },
  ];
  const update = (i, field, value) => {
    const copy = [...list];
    copy[i] = { ...copy[i], [field]: Number(value) };
    onChange(copy);
  };

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <p className="mb-4 text-sm text-slate-600">
          Anchor points used for linear interpolation of low and high multiples across SDE.
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="py-2">SDE</th>
                <th className="py-2">Low multiple</th>
                <th className="py-2">High multiple</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {list.map((a, i) => (
                <tr key={i}>
                  <td className="py-2 pr-3"><Input type="number" value={a.pebitda} onChange={(e) => update(i, "pebitda", e.target.value)} className="h-10 w-40" /></td>
                  <td className="py-2 pr-3"><Input type="number" step="0.01" value={a.low} onChange={(e) => update(i, "low", e.target.value)} className="h-10 w-32" /></td>
                  <td className="py-2"><Input type="number" step="0.01" value={a.high} onChange={(e) => update(i, "high", e.target.value)} className="h-10 w-32" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="flex justify-end">
        <button onClick={onSave} className="btn-primary"><Save className="h-4 w-4" /> Save anchors</button>
      </div>
    </div>
  );
}

function EquipmentEditor({ settings, onChange, onSave }) {
  const levels = settings.equipment_levels || {};
  const conditions = settings.equipment_condition_adj || {};

  const updateLevel = (key, field, value) => {
    const next = { ...levels, [key]: { ...(levels[key] || {}), [field]: field === "label" ? value : Number(value) } };
    onChange({ equipment_levels: next });
  };
  const updateCondition = (key, value) => {
    const next = { ...conditions, [key]: Number(value) };
    onChange({ equipment_condition_adj: next });
  };

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <div className="overline">Equipment depreciation</div>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <Label className="mb-2 block">Useful life (years)</Label>
            <Input
              type="number"
              value={settings.equipment_useful_life_years ?? 10}
              onChange={(e) => onChange({ equipment_useful_life_years: e.target.value })}
              className="h-12"
            />
          </div>
        </div>

        <div className="mt-6 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="py-2">Level</th>
                <th className="py-2">Label</th>
                <th className="py-2">Depreciation rate (0–1)</th>
                <th className="py-2">Residual after 10 yrs</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {["premium", "standard", "basic"].map((key) => {
                const cfg = levels[key] || {};
                return (
                  <tr key={key}>
                    <td className="py-2 font-medium text-slate-800 capitalize">{key}</td>
                    <td className="py-2"><Input value={cfg.label ?? ""} onChange={(e) => updateLevel(key, "label", e.target.value)} className="h-10" /></td>
                    <td className="py-2"><Input type="number" step="0.0001" value={cfg.depreciation_rate ?? ""} onChange={(e) => updateLevel(key, "depreciation_rate", e.target.value)} className="h-10 w-32" /></td>
                    <td className="py-2"><Input type="number" step="0.01" value={cfg.residual_pct ?? ""} onChange={(e) => updateLevel(key, "residual_pct", e.target.value)} className="h-10 w-32" /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <div className="overline">Condition adjustments</div>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[
            ["excellent", "Excellent"],
            ["good", "Good"],
            ["average", "Average"],
            ["poor", "Poor"],
            ["significant_replacement", "Significant replacement required"],
          ].map(([key, label]) => (
            <div key={key}>
              <Label className="mb-2 block">{label}</Label>
              <Input
                type="number"
                step="0.01"
                value={conditions[key] ?? 0}
                onChange={(e) => updateCondition(key, e.target.value)}
                className="h-12"
              />
            </div>
          ))}
        </div>
        <p className="mt-4 text-xs text-slate-500">Values are decimal adjustments applied to the age-based equipment value. e.g. 0.10 = +10%, -0.20 = -20%.</p>
      </div>

      <div className="flex justify-end">
        <button onClick={onSave} className="btn-primary" data-testid="save-equipment-btn">
          <Save className="h-4 w-4" /> Save equipment settings
        </button>
      </div>
    </div>
  );
}

function InterimReportDialog({ lead, onClose, token }) {
  const [lowM, setLowM] = useState("");
  const [highM, setHighM] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (lead) {
      setLowM(lead.result?.adjusted_multiple_low?.toFixed(2) ?? "");
      setHighM(lead.result?.adjusted_multiple_high?.toFixed(2) ?? "");
      setNotes("");
    }
  }, [lead]);

  const open = !!lead;

  const generate = async () => {
    if (!lead) return;
    const low = Number(lowM);
    const high = Number(highM);
    if (!low || !high || low <= 0 || high < low) {
      toast.error("Please provide valid low and high multiples (low ≤ high).");
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/admin/valuations/${lead.id}/interim-report`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Admin-Token": token },
        body: JSON.stringify({ low_multiple: low, high_multiple: high, broker_notes: notes }),
      });
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `GymSales-Interim-${lead.id.slice(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Interim report generated.");
      onClose();
    } catch (e) {
      toast.error(e.message || "Failed to generate interim report");
    } finally {
      setBusy(false);
    }
  };

  const est = lead && Number(lowM) > 0 && Number(highM) > 0
    ? `${currency((lead.result?.normalised_pebitda || 0) * Number(lowM))} – ${currency((lead.result?.normalised_pebitda || 0) * Number(highM))}`
    : "—";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="interim-dialog">
        <DialogHeader>
          <DialogTitle>Broker interim report</DialogTitle>
          <DialogDescription>
            {lead ? `${lead.lead?.first_name} ${lead.lead?.last_name} — ${lead.lead?.business_name}` : ""}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="mb-2 block">Low multiple</Label>
              <Input type="number" step="0.01" value={lowM} onChange={(e) => setLowM(e.target.value)} data-testid="interim-low-multiple" className="h-11" />
            </div>
            <div>
              <Label className="mb-2 block">High multiple</Label>
              <Input type="number" step="0.01" value={highM} onChange={(e) => setHighM(e.target.value)} data-testid="interim-high-multiple" className="h-11" />
            </div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm">
            <div className="text-xs uppercase tracking-wider text-slate-500">Estimated range (SDE × override)</div>
            <div className="mt-1 font-semibold text-slate-900" data-testid="interim-preview-range">{est}</div>
            {lead && <div className="mt-1 text-xs text-slate-500">SDE: {currency(lead.result?.normalised_pebitda)}</div>}
          </div>
          <div>
            <Label className="mb-2 block">Broker notes (optional, printed on the report)</Label>
            <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="interim-notes" />
          </div>
        </div>
        <DialogFooter>
          <button onClick={onClose} className="btn-ghost" data-testid="interim-cancel-btn">Cancel</button>
          <button onClick={generate} disabled={busy} className="btn-primary" data-testid="interim-generate-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><FileEdit className="h-4 w-4" /> Generate interim PDF</>}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
